import React, { useState, useEffect } from 'react';
import './MetricsPanel.css';
import { fetchThresholds, resolveAlert } from '../services/api';
import { formatDateTime } from '../utils/dateFormat';
import { useAuth } from '../context/AuthContext';

const MetricsPanel = ({ site, onAlertResolved }) => {
  const [thresholds, setThresholds] = useState(null);
  const [resolvingId, setResolvingId] = useState(null);
  const { authData } = useAuth();

  // Re-evaluate Agent Down on a timer: when the agent stops reporting there is
  // no WebSocket push, so the UI must tick locally against lastUpdated.
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 15000);
    return () => clearInterval(id);
  }, []);

  // Load thresholds for this site - must be called before any early returns
  useEffect(() => {
    if (!site?.id) return;
    
    const loadThresholds = async () => {
      try {
        const siteThresholds = await fetchThresholds(site.id);
        setThresholds(siteThresholds);
      } catch (err) {
        console.error('Failed to load thresholds:', err);
        // Use default thresholds on error
        setThresholds({
          cpu: { warning: 80, critical: 90 },
          memory: { warning: 85, critical: 95 },
          disk: { warning: 80, critical: 90 },
          responseTime: { warning: 2000, critical: 3000 },
          loadAverage: { warning: 200, critical: 300 },
          temperature: { warning: 60, critical: 70 },
          unrecognizedExits: { warning: 4, critical: 6 },
          lastPayment: { warning: 120, critical: 180 }
        });
      }
    };

    loadThresholds();
  }, [site?.id]);
  
  if (!site) return null;
  
  // Check if site has heartbeat data (is being monitored)
  const isMonitored = site.heartbeat && site.heartbeat.status !== undefined && site.heartbeat.status !== null;

  // Agent Down: explicit alert, OR last metrics older than 3 minutes.
  // Do not trust heartbeat.status === 'online' alone — that value is from the
  // last successful report and stays "online" until a new report arrives.
  const hasAgentDownAlert = !!(site.alerts && site.alerts.some(alert =>
    alert.type === 'agent_down' || alert.message === 'Monitoring agent is not responding'
  ));
  const heartbeatStatusValue = site.heartbeat?.status;
  const isHeartbeatOnline = heartbeatStatusValue === 'online' || heartbeatStatusValue === 'up';
  const THREE_MINUTES_MS = 3 * 60 * 1000;
  let isAgentDown = false;
  if (site.status !== 'not-monitored' && isMonitored) {
    if (hasAgentDownAlert) {
      isAgentDown = true;
    } else if (site.lastUpdated) {
      const timeSinceLastUpdate = nowTick - new Date(site.lastUpdated).getTime();
      isAgentDown = timeSinceLastUpdate > THREE_MINUTES_MS;
    } else if (!isHeartbeatOnline && site.status === 'critical') {
      isAgentDown = true;
    }
  }
  // Live metrics only when monitored and agent is reporting
  const hasLiveData = isMonitored && !isAgentDown;

  const handleResolveAlert = async (e, alertId) => {
    e.stopPropagation(); // Prevent any click events from bubbling
    if (window.confirm('Resolve this alert?')) {
      setResolvingId(alertId);
      try {
        const userName = authData?.userName || null;
        await resolveAlert(alertId, userName);
        // Call the callback to refresh site data
        if (onAlertResolved) {
          await onAlertResolved();
        }
      } catch (error) {
        console.error('Failed to resolve alert:', error);
        alert('Failed to resolve alert. Please try again.');
      } finally {
        setResolvingId(null);
      }
    }
  };


  const getPercentageColor = (percentage, isHeartbeat = false, isMemory = false, isDisk = false) => {
    // For agent heartbeat, 100% means online (good), so use green
    if (isHeartbeat) {
      if (percentage >= 100) return '#10b981'; // Green for online
      return '#ef4444'; // Red for offline
    }
    
    // Get thresholds (use defaults if not loaded yet)
    const cpuWarning = thresholds?.cpu?.warning ?? 80;
    const cpuCritical = thresholds?.cpu?.critical ?? 90;
    const memoryWarning = thresholds?.memory?.warning ?? 85;
    const memoryCritical = thresholds?.memory?.critical ?? 95;
    const diskWarning = thresholds?.disk?.warning ?? 80;
    const diskCritical = thresholds?.disk?.critical ?? 90;
    
    // For memory usage: use site-specific thresholds
    if (isMemory) {
      if (percentage <= memoryWarning) return '#10b981'; // Green
      if (percentage <= memoryCritical) return '#f59e0b'; // Yellow
      return '#ef4444'; // Red
    }
    
    // For disk usage: use site-specific thresholds
    if (isDisk) {
      if (percentage <= diskWarning) return '#10b981'; // Green
      if (percentage <= diskCritical) return '#f59e0b'; // Yellow
      return '#ef4444'; // Red
    }
    
    // For CPU usage: use site-specific thresholds
    if (percentage >= cpuCritical) return '#ef4444'; // Red
    if (percentage >= cpuWarning) return '#f59e0b'; // Yellow
    return '#10b981'; // Green for good status
  };

  const getStatusColor = (status) => {
    const statusLower = String(status).toLowerCase();
    if (statusLower === 'up' || statusLower === 'online' || statusLower === 'normal') return '#10b981'; // Green for online
    if (statusLower === 'down' || statusLower === 'offline' || statusLower === 'critical') return '#ef4444';
    if (statusLower === 'warning') return '#f59e0b';
    return '#9ca3af';
  };

  const getLoadAverageStatus = (value) => {
    // Descending thresholds: 0 = Critical, 1-4 = Warning, >4 = Normal
    const loadValue = parseFloat(value) || 0;
    if (loadValue === 0) return 'critical'; // 0 = Critical
    if (loadValue >= 1 && loadValue <= 4) return 'warning'; // 1-4 = Warning
    return 'normal'; // >4 = Normal
  };

  const getUnrecognizedExitsStatus = (value) => {
    // Thresholds: <4 = Normal, 4-6 = Warning, >6 = Critical
    const exitsValue = parseInt(value) || 0;
    const warningThreshold = thresholds?.unrecognizedExits?.warning ?? 4;
    const criticalThreshold = thresholds?.unrecognizedExits?.critical ?? 6;
    
    if (exitsValue > criticalThreshold) return 'critical'; // >6 = Critical
    if (exitsValue >= warningThreshold && exitsValue <= criticalThreshold) return 'warning'; // 4-6 = Warning
    return 'normal'; // <4 = Normal
  };

  const getLastPaymentStatus = (creationDate) => {
    if (!creationDate) return 'unknown';
    const creationMs = new Date(creationDate).getTime();
    if (Number.isNaN(creationMs)) return 'unknown';
    const minutesSince = Math.floor((Date.now() - creationMs) / 60000);
    const warningThreshold = thresholds?.lastPayment?.warning ?? 120;
    const criticalThreshold = thresholds?.lastPayment?.critical ?? 180;
    if (minutesSince > criticalThreshold) return 'critical';
    if (minutesSince > warningThreshold) return 'warning';
    return 'normal';
  };

  const MetricCard = ({ title, value, unit, percentage, maxValue, maxPrefix = 'Total', status, isHeartbeat = false, isMemory = false, isDisk = false, additionalInfo }) => {
    const isNA = value === 'N/A' || value === 'No Data' || value === null || value === undefined;
    const showPercentage = percentage !== null && percentage !== undefined && !isNA;
    const showBar = showPercentage && typeof percentage === 'number';
    const color = getPercentageColor(percentage, isHeartbeat, isMemory, isDisk);
    const statusLower = status ? String(status).toLowerCase() : '';
    const forceRedValue = isNA && (statusLower === 'down' || statusLower === 'offline' || statusLower === 'critical');
    
    return (
      <div className={`metric-card${forceRedValue ? ' metric-card-nodata' : ''}`}>
        <div className="metric-header">
          <h3 className="metric-title">{title}</h3>
          {showPercentage && (
            <span 
              className="metric-percentage"
              style={{ color: color }}
            >
              {typeof percentage === 'number' ? parseFloat(percentage.toFixed(2)) : percentage}%
            </span>
          )}
          {status && status !== 'unknown' && (
            <span 
              className="metric-status"
              style={{ color: getStatusColor(status) }}
            >
              {String(status).toUpperCase()}
            </span>
          )}
        </div>
        <div className="metric-value">
          <span className="value" style={forceRedValue ? { color: '#ef4444' } : undefined}>{isNA ? (value === 'No Data' ? 'No Data' : 'N/A') : value}</span>
          {unit && !isNA && <span className="unit">{unit}</span>}
        </div>
        {showBar && (
          <div className="metric-bar">
            <div 
              className="metric-bar-fill"
              style={{ 
                width: `${Math.min(percentage, 100)}%`,
                backgroundColor: color
              }}
            />
          </div>
        )}
        {maxValue && !isNA && (
          <div className="metric-max">
            {maxPrefix}: {maxValue}
          </div>
        )}
        {additionalInfo && !forceRedValue && (
          <div className="metric-additional">
            {additionalInfo}
          </div>
        )}
      </div>
    );
  };

  // Dynamic metric renderer - handles any metric structure
  const renderDynamicMetric = (key, value) => {
    // Skip known special fields
    if (['id', 'siteId', 'name', 'ip', 'location', 'zone', 'type', 'timestamp', 'lastUpdated', 'alerts', 'heartbeat', 'status', 'responseTime', 'publicIp', 'myParkVersion', 'agentVersion', 'unrecognizedExits'].includes(key)) {
      return null;
    }

    const sectionTitle = key === 'myParkPayment' ? 'MyPark Payment'
      : key === 'cameras' ? 'Cameras'
      : key === 'controllers' ? 'Controllers'
      : key.charAt(0).toUpperCase() + key.slice(1);

    // Agent Down: force red No Data for list widgets (cameras, controllers, payments, etc.)
    if (isAgentDown && (Array.isArray(value) || value === null || value === undefined ||
        ['cameras', 'controllers', 'myParkPayment', 'gates'].includes(key))) {
      const items = Array.isArray(value) ? value : [];
      return (
        <div key={key} className="metric-section metric-section-nodata">
          <h3 className="metric-section-title">{sectionTitle}</h3>
          <div className="metric-list">
            {items.length === 0 ? (
              <div className="metric-list-item metric-list-item-nodata">
                <span className="metric-list-value metric-list-value-nodata">No Data</span>
              </div>
            ) : (
              items.map((item, index) => (
                <div key={item.id || index} className="metric-list-item metric-list-item-nodata">
                  <div className="metric-list-header">
                    <div className="metric-list-name-section">
                      <span className="metric-list-name">{item.name || item.id || `Item ${index + 1}`}</span>
                      {item.ip && (
                        <span className="metric-list-ip" style={{ fontWeight: 'bold' }}>{item.ip}</span>
                      )}
                    </div>
                    <span className="metric-list-status" style={{ color: '#ef4444', backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
                      NO DATA
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      );
    }

    // Skip if value is null, undefined, or empty array
    if (value === null || value === undefined) {
      return null;
    }

    // If not monitored, show N/A for all dynamic metrics
    if (!hasLiveData) {
      if (Array.isArray(value)) {
        return (
          <div key={key} className="metric-section">
            <h3 className="metric-section-title">{sectionTitle}</h3>
            <div className="metric-list">
              <div className="metric-list-item">
                <span className="metric-list-value">N/A</span>
              </div>
            </div>
          </div>
        );
      }
      return (
        <MetricCard
          key={key}
          title={sectionTitle}
          value="N/A"
        />
      );
    }

    // Handle arrays (cameras, gates, controllers, myParkPayment, etc.)
    if (Array.isArray(value)) {
      // Only render if array has items
      if (value.length === 0) {
        return null;
      }
      
      return (
        <div key={key} className="metric-section">
          <h3 className="metric-section-title">{sectionTitle}</h3>
          <div className="metric-list">
            {value.map((item, index) => (
              <div key={item.id || index} className="metric-list-item">
                <div className="metric-list-header">
                  <div className="metric-list-name-section">
                    <span className="metric-list-name">{item.name || item.id || `Item ${index + 1}`}</span>
                    {item.ip && (
                      <span className="metric-list-ip" style={{ fontWeight: 'bold' }}>{item.ip}</span>
                    )}
                  </div>
                  {item.availability && (
                    <span 
                      className="metric-list-status"
                      style={{ color: getStatusColor(item.availability) }}
                    >
                      {item.availability.toUpperCase()}
                    </span>
                  )}
                </div>
                {Object.entries(item).map(([subKey, subValue]) => {
                  if (['id', 'name', 'ip', 'availability', 'responseTime'].includes(subKey)) return null;
                  // Skip null/undefined values unless it's lastIdentificationTime (show N/A for that)
                  if (subValue === null || subValue === undefined) {
                    if (subKey === 'lastIdentificationTime') {
                      return (
                        <div key={subKey} className="metric-list-detail">
                          <span className="metric-list-label">{subKey === 'lastEvent' ? 'lastAgentReport' : subKey}:</span>
                          <span className="metric-list-value">N/A</span>
                        </div>
                      );
                    }
                    return null;
                  }
                  // Map field names to display labels
                  const displayLabel = subKey === 'lastEvent' ? 'lastAgentReport' : subKey;
                  return (
                    <div key={subKey} className="metric-list-detail">
                      <span className="metric-list-label">{displayLabel}:</span>
                      <span className="metric-list-value">
                        {subKey.includes('date') || subKey.includes('event') || subKey.includes('seen') || subKey.includes('Event') || subKey.includes('Date') || subKey.includes('Time') || subKey.includes('identification')
                          ? formatDateTime(subValue) 
                          : String(subValue)}
                      </span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Handle objects (nested metrics)
    if (typeof value === 'object' && value !== null) {
      // Check if it's a simple metric object with common patterns
      if (value.usage !== undefined || value.used !== undefined || value.value !== undefined || value.availability !== undefined) {
        // If not monitored, show N/A
        if (!hasLiveData) {
          return (
            <MetricCard
              key={key}
              title={sectionTitle}
              value={isAgentDown ? 'No Data' : 'N/A'}
              status={isAgentDown ? 'down' : 'unknown'}
            />
          );
        }
        
        let displayValue, unit, percentage, status;
        
        if (value.usage !== undefined) {
          displayValue = value.usage;
          unit = '%';
          percentage = value.usage;
        } else if (value.used !== undefined) {
          displayValue = value.used;
          unit = value.total ? 'GB' : '';
          percentage = value.total ? (value.used / value.total) * 100 : undefined;
        } else if (value.value !== undefined) {
          displayValue = value.value;
          unit = value.unit || '';
          status = value.status;
        } else if (value.availability !== undefined) {
          displayValue = value.availability;
          status = value.availability;
        }

        return (
          <MetricCard
            key={key}
            title={sectionTitle}
            value={displayValue}
            unit={unit}
            percentage={percentage}
            maxValue={value.total ? `${value.total} ${value.total > 100 ? 'GB' : ''}` : undefined}
            status={status}
          />
        );
      }
      
      // Complex nested object - render as section
      // If not monitored, show N/A
      if (!hasLiveData) {
        return (
          <div key={key} className="metric-section">
            <h3 className="metric-section-title">{sectionTitle}</h3>
            <div className="metric-details">
              <div className="metric-detail-row">
                <span className="metric-detail-value" style={isAgentDown ? { color: '#ef4444' } : undefined}>
                  {isAgentDown ? 'No Data' : 'N/A'}
                </span>
              </div>
            </div>
          </div>
        );
      }
      
      return (
        <div key={key} className="metric-section">
          <h3 className="metric-section-title">{sectionTitle}</h3>
          <div className="metric-details">
            {Object.entries(value).map(([subKey, subValue]) => (
              <div key={subKey} className="metric-detail-row">
                <span className="metric-detail-label">{subKey}:</span>
                <span className="metric-detail-value">
                  {typeof subValue === 'object' ? JSON.stringify(subValue) : String(subValue)}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Handle simple values
    // If not monitored, show N/A
    if (!hasLiveData) {
      return (
        <MetricCard
          key={key}
          title={sectionTitle}
          value={isAgentDown ? 'No Data' : 'N/A'}
          status={isAgentDown ? 'down' : 'unknown'}
        />
      );
    }
    
    return (
      <MetricCard
        key={key}
        title={sectionTitle}
        value={value}
      />
    );
  };

  // Get all metric keys (excluding metadata)
  const metricKeys = Object.keys(site).filter(key => 
    !['id', 'siteId', 'name', 'ip', 'location', 'zone', 'type', 'timestamp', 'lastUpdated', 'alerts', 'publicIp', 'myParkVersion', 'agentVersion'].includes(key)
  );

  // Separate standard metrics from dynamic ones
  const standardMetrics = [];
  const dynamicMetrics = [];

  metricKeys.forEach(key => {
    if (['heartbeat', 'cpu', 'memory', 'disk', 'diskSpace', 'myParkServer', 'dbAvailability', 'windowsFirewall', 'pinpadStatus', 'lastPayment'].includes(key)) {
      standardMetrics.push(key);
    } else {
      dynamicMetrics.push(key);
    }
  });

  // Helper to format percentage safely (rounded to 2 decimal places)
  const getPercentage = (used, total) => {
    if (!used || !total || total === 0) return 0;
    return parseFloat(((used / total) * 100).toFixed(2));
  };

  return (
    <div className="metrics-panel">
      {/* Standard Metrics Grid - Always show all metrics */}
      <div className="metrics-grid">
        {/* Agent Heartbeat - Always shown */}
        {(() => {
          const isNotMonitored = site.status === 'not-monitored';
          let heartbeatValue, heartbeatPercentage, heartbeatStatus;
          
          if (isNotMonitored) {
            heartbeatValue = 'N/A';
            heartbeatPercentage = null;
            heartbeatStatus = 'unknown';
          } else if (isAgentDown) {
            heartbeatValue = 'Offline';
            heartbeatPercentage = 0;
            heartbeatStatus = 'offline';
          } else if (isHeartbeatOnline) {
            heartbeatValue = 'Online';
            heartbeatPercentage = 100;
            heartbeatStatus = 'online';
          } else if (site.heartbeat && site.heartbeat.status !== undefined && site.heartbeat.status !== null) {
            heartbeatValue = heartbeatStatusValue === 'offline' || heartbeatStatusValue === 'down' ? 'Offline' : 'N/A';
            heartbeatPercentage = heartbeatStatusValue === 'offline' || heartbeatStatusValue === 'down' ? 0 : null;
            heartbeatStatus = heartbeatStatusValue === 'offline' || heartbeatStatusValue === 'down' ? 'offline' : 'unknown';
          } else {
            heartbeatValue = 'N/A';
            heartbeatPercentage = null;
            heartbeatStatus = 'unknown';
          }
          
          return (
            <MetricCard
              title="Agent Heartbeat"
              value={heartbeatValue}
              percentage={heartbeatPercentage}
              status={heartbeatStatus}
              isHeartbeat={true}
              maxPrefix="Version"
              maxValue={!isNotMonitored ? (site.agentVersion || '1.0.0') : null}
            />
          );
        })()}
        
        {/* Response Time */}
        <MetricCard
          title="Response Time"
          value={hasLiveData && site.heartbeat?.responseTime !== undefined ? site.heartbeat.responseTime : (isAgentDown ? 'No Data' : 'N/A')}
          unit={hasLiveData && site.heartbeat?.responseTime !== undefined ? 'ms' : ''}
          status={hasLiveData && site.heartbeat?.responseTime !== undefined
            ? (site.heartbeat.responseTime <= 2000 ? 'normal' : site.heartbeat.responseTime <= 3000 ? 'warning' : 'critical')
            : (isAgentDown ? 'down' : 'unknown')}
        />

        {/* CPU Usage */}
        <MetricCard
          title="CPU Usage"
          value={hasLiveData && site.cpu?.usage !== undefined ? site.cpu.usage : (isAgentDown ? 'No Data' : 'N/A')}
          unit={hasLiveData && site.cpu?.usage !== undefined ? '%' : ''}
          percentage={hasLiveData && site.cpu?.usage !== undefined ? site.cpu.usage : null}
          status={isAgentDown ? 'down' : undefined}
        />

        {/* Memory Usage */}
        {(() => {
          const memoryPercent = hasLiveData && site.memory?.used && site.memory?.total ? getPercentage(site.memory.used, site.memory.total) : null;
          return (
            <MetricCard
              title="Memory Usage"
              value={hasLiveData && site.memory?.used !== undefined ? site.memory.used : (isAgentDown ? 'No Data' : 'N/A')}
              unit={hasLiveData && site.memory?.used !== undefined ? 'GB' : ''}
              percentage={memoryPercent}
              maxValue={hasLiveData && site.memory?.total ? `${site.memory.total} GB` : null}
              isMemory={true}
              status={isAgentDown ? 'down' : undefined}
            />
          );
        })()}

        {/* Disk Usage */}
        {(() => {
          const disk = site.disk || site.diskSpace;
          const diskPercent = hasLiveData && disk?.usagePercent !== undefined ? disk.usagePercent : (hasLiveData && disk?.used && disk?.total ? getPercentage(disk.used, disk.total) : null);
          return (
            <MetricCard
              title="Disk Usage"
              value={hasLiveData && disk?.used !== undefined ? disk.used : (isAgentDown ? 'No Data' : 'N/A')}
              unit={hasLiveData && disk?.used !== undefined ? 'GB' : ''}
              percentage={diskPercent}
              maxValue={hasLiveData && disk?.total ? `${disk.total} GB` : null}
              isDisk={true}
              status={isAgentDown ? 'down' : undefined}
            />
          );
        })()}

        {/* MyPark Server */}
        <MetricCard
          title="MyPark Server"
          value={hasLiveData && site.myParkServer?.availability ? site.myParkServer.availability : (isAgentDown ? 'No Data' : 'N/A')}
          status={hasLiveData && site.myParkServer?.availability ? site.myParkServer.availability : (isAgentDown ? 'down' : 'unknown')}
          additionalInfo={hasLiveData && site.myParkVersion ? `Version: ${site.myParkVersion}` : null}
        />

        {/* Pinpad Status */}
        {(() => {
          const pinpad = site.pinpadStatus;
          const hasData = pinpad && (pinpad.status === 'active' || pinpad.status === 'inactive');
          let value = isAgentDown ? 'No Data' : 'N/A';
          let status = isAgentDown ? 'down' : 'unknown';
          if (hasLiveData && hasData) {
            if (pinpad.status === 'active') {
              value = 'Active';
              status = 'up';
            } else {
              value = 'Inactive';
              status = 'down';
            }
          }
          return (
            <MetricCard
              title="Pinpad Status"
              value={value}
              status={status}
            />
          );
        })()}

        {/* Load Average per Camera */}
        {(() => {
          const loadValue = hasLiveData && site.cpu?.loadAverage !== undefined ? parseInt(site.cpu.loadAverage) : null;
          return (
            <MetricCard
              title="Load Average per Camera"
              value={loadValue !== null ? loadValue : (isAgentDown ? 'No Data' : 'N/A')}
              unit={loadValue !== null ? 'events/hour' : ''}
              status={loadValue !== null ? getLoadAverageStatus(loadValue) : (isAgentDown ? 'down' : 'unknown')}
            />
          );
        })()}

        {/* Last Payment */}
        {(() => {
          const creationDate = site.lastPayment?.creationDate;
          const hasData = hasLiveData && !!creationDate;
          return (
            <MetricCard
              title="Last Payment"
              value={hasData ? formatDateTime(creationDate) : (isAgentDown ? 'No Data' : 'N/A')}
              status={hasData ? getLastPaymentStatus(creationDate) : (isAgentDown ? 'down' : 'unknown')}
            />
          );
        })()}

        {/* Exists with No Duration */}
        {(() => {
          const exitsValue = hasLiveData && site.unrecognizedExits !== undefined ? parseInt(site.unrecognizedExits) : null;
          return (
            <MetricCard
              title="Exists with No Duration"
              value={exitsValue !== null ? exitsValue : (isAgentDown ? 'No Data' : 'N/A')}
              unit={exitsValue !== null ? 'exits (last 15 min)' : ''}
              status={exitsValue !== null ? getUnrecognizedExitsStatus(exitsValue) : (isAgentDown ? 'down' : 'unknown')}
            />
          );
        })()}

        {/* DB Availability */}
        {(() => {
          if (isAgentDown) {
            return (
              <MetricCard
                title="DB Availability"
                value="No Data"
                status="down"
              />
            );
          }
          const dbAv = site.dbAvailability;
          const status = dbAv?.status;
          const value = (status === 'OK' || status === 'FAILED') ? status : 'N/A';
          const pl = dbAv?.passwordLifetime;
          const daysLabel = pl !== undefined && pl !== null
            ? (pl === 'LIFETIME' ? 'LIFETIME' : String(pl))
            : '—';
          const additionalInfo = hasLiveData && dbAv
            ? `Days before Password Expired: ${daysLabel}`
            : null;
          return (
            <MetricCard
              title="DB Availability"
              value={value}
              status={status === 'OK' ? 'up' : status === 'FAILED' ? 'down' : 'unknown'}
              additionalInfo={additionalInfo}
            />
          );
        })()}

        {/* Windows Firewall */}
        {(() => {
          const fw = site.windowsFirewall;
          const hasData = fw && fw.status !== undefined && fw.status !== null;
          let value = isAgentDown ? 'No Data' : 'N/A';
          let status = isAgentDown ? 'down' : 'unknown';
          let additionalInfo = null;
          if (hasLiveData && hasData) {
            if (fw.status === 'up') {
              value = 'Up';
              status = 'up';
            } else if (fw.status === 'down') {
              value = 'Down';
              status = 'down';
            } else {
              value = 'Unknown';
              status = 'unknown';
            }
            if (Array.isArray(fw.disabledProfiles) && fw.disabledProfiles.length > 0) {
              additionalInfo = `Off: ${fw.disabledProfiles.join(', ')}`;
            } else if (fw.profiles && typeof fw.profiles === 'object') {
              const parts = Object.entries(fw.profiles).map(([name, on]) => `${name}: ${on ? 'ON' : 'OFF'}`);
              if (parts.length > 0) additionalInfo = parts.join(', ');
            } else if (fw.profile) {
              additionalInfo = `Active profile: ${fw.profile}`;
            }
          }
          return (
            <MetricCard
              title="Windows Firewall"
              value={value}
              status={status}
              additionalInfo={additionalInfo}
            />
          );
        })()}
      </div>

      {/* Dynamic Metrics - Arrays and Custom Metrics (including Agent Down → No Data) */}
      {isMonitored && (
        <div className="dynamic-metrics">
          {(isAgentDown
            ? [...new Set([...dynamicMetrics, 'cameras', 'controllers', 'myParkPayment'])]
            : dynamicMetrics
          ).map(key => renderDynamicMetric(key, site[key])).filter(Boolean)}
        </div>
      )}

      {site.alerts && site.alerts.length > 0 && (
        <div className="metrics-alerts">
          <h3 className="metrics-alerts-title">Active Alerts for this Site</h3>
          <div className="metrics-alerts-list">
            {site.alerts.map((alert, index) => (
              <div key={`${alert.id}-${index}-${alert.timestamp || ''}`} className={`metrics-alert metrics-alert-${alert.severity}`}>
                <span className="metrics-alert-icon">
                  {alert.severity === 'critical' ? '!' : '⚠'}
                </span>
                <span className="metrics-alert-message">{alert.message}</span>
                <span className="metrics-alert-time">
                  {formatDateTime(alert.timestamp)}
                </span>
                <button
                  className="resolve-btn"
                  onClick={(e) => handleResolveAlert(e, alert.id)}
                  disabled={resolvingId === alert.id}
                  title="Resolve alert"
                >
                  {resolvingId === alert.id ? 'Resolving...' : 'Resolve'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MetricsPanel;

